/**
 * Compact 7-zone armor diagram (per-side stat panel).
 *
 * SVG humanoid silhouette — head atop, chassis center (front+rear stack
 * vertically), arms flanking, legs as feet. Each zone fills with the
 * accent color modulated by health:
 *   - armor remaining → solid accent, opacity ∝ armor%
 *   - exposed (armor=0, internal > 0) → danger-color outline + diagonal
 *     hatch fill (a "breach" pattern)
 *   - destroyed (armor=0, internal=0) → empty + dim border
 *
 * The diagram doesn't surface absolute numbers — those go in a small
 * legend row below the silhouette when `showLegend` is on. Keep it
 * readable at small sizes (it has to live in a corner panel).
 */

import type { CSSProperties } from "react";
import type { MechState, ZoneState } from "@pilot-protocol/replay-parser";
import { zoneHealth, type ZoneHealth } from "./derive";

export interface ZoneArmorDiagramProps {
  mech: MechState;
  /** Side accent for "healthy armor" fill. */
  accent: string;
  /** Show the small numeric legend under the silhouette. */
  showLegend?: boolean;
  /** Max armor per zone (engine defaults to 4 for non-head zones). */
  maxArmor?: number;
  /** Max internal per zone (engine defaults to 4). */
  maxInternal?: number;
}

interface ZoneShape {
  key: keyof Pick<
    MechState,
    "head" | "front_ct" | "rear_ct" | "l_side" | "r_side" | "l_leg" | "r_leg"
  >;
  label: string;
  /** SVG path for this zone (viewBox 0 0 100 140). */
  d: string;
}

// Hand-tuned humanoid silhouette in a 100×140 viewBox. Front_ct is the
// large chest plate; rear_ct sits inside as a smaller inset to read as
// "behind". Legs are joined at hip and split at knee for clarity.
const ZONES: ReadonlyArray<ZoneShape> = [
  { key: "head", label: "H", d: "M40,8 L60,8 L62,28 L38,28 Z" },
  { key: "front_ct", label: "CT", d: "M30,32 L70,32 L70,74 L30,74 Z" },
  { key: "rear_ct", label: "RT", d: "M40,42 L60,42 L60,64 L40,64 Z" },
  { key: "l_side", label: "L", d: "M10,34 L28,34 L28,76 L10,76 Z" },
  { key: "r_side", label: "R", d: "M72,34 L90,34 L90,76 L72,76 Z" },
  { key: "l_leg", label: "LL", d: "M30,78 L48,78 L46,134 L30,134 Z" },
  { key: "r_leg", label: "RL", d: "M52,78 L70,78 L70,134 L54,134 Z" },
];

const rootStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
};

const svgStyle: CSSProperties = {
  width: "100%",
  maxWidth: 96,
  height: "auto",
};

const legendStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  gap: "2px 6px",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  width: "100%",
  alignItems: "center",
};

function fillFor(h: ZoneHealth, accent: string): string {
  switch (h.state) {
    case "armor":
      return accent;
    case "internal":
      return "url(#pp-breach)";
    case "destroyed":
      return "transparent";
  }
}

function strokeFor(h: ZoneHealth): string {
  switch (h.state) {
    case "armor":
      return "var(--pp-border)";
    case "internal":
      return "var(--pp-danger)";
    case "destroyed":
      return "var(--pp-text-muted)";
  }
}

function opacityFor(h: ZoneHealth): number {
  if (h.state === "armor") return 0.35 + 0.6 * h.armorPct;
  if (h.state === "internal") return 1;
  return 0.25;
}

export function ZoneArmorDiagram({
  mech,
  accent,
  showLegend = false,
  maxArmor = 4,
  maxInternal = 4,
}: ZoneArmorDiagramProps) {
  const healths: Array<{ shape: ZoneShape; zone: ZoneState; h: ZoneHealth }> = ZONES.map(
    (shape) => {
      const zone = mech[shape.key];
      return { shape, zone, h: zoneHealth(zone, maxArmor, maxInternal) };
    },
  );
  return (
    <div style={rootStyle}>
      <svg viewBox="0 0 100 140" style={svgStyle} aria-label="Zone armor">
        <defs>
          {/* Diagonal hatch pattern for exposed/internal zones. */}
          <pattern
            id="pp-breach"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="6"
              stroke="var(--pp-danger)"
              strokeWidth="2"
            />
          </pattern>
        </defs>
        {healths.map(({ shape, h }) => (
          <path
            key={shape.key}
            d={shape.d}
            fill={fillFor(h, accent)}
            fillOpacity={opacityFor(h)}
            stroke={strokeFor(h)}
            strokeWidth={1}
          />
        ))}
      </svg>
      {showLegend ? (
        <div style={legendStyle} aria-hidden>
          {healths.map(({ shape, zone, h }) => (
            <ZoneRow key={shape.key} shape={shape} zone={zone} h={h} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface ZoneRowProps {
  shape: ZoneShape;
  zone: ZoneState;
  h: ZoneHealth;
}

function ZoneRow({ shape, zone, h }: ZoneRowProps) {
  const stateLabel =
    h.state === "destroyed" ? "✕" : h.state === "internal" ? "BR" : "·";
  return (
    <>
      <span style={{ fontWeight: "var(--pp-weight-bold)" }}>{shape.label}</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {zone.armor}/{zone.internal}
      </span>
      <span
        style={{
          color:
            h.state === "internal"
              ? "var(--pp-danger)"
              : h.state === "destroyed"
                ? "var(--pp-text-muted)"
                : "var(--pp-text-muted)",
        }}
      >
        {stateLabel}
      </span>
    </>
  );
}
